import React, { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { getSentAppreciations, getReceivedAppreciations, getLeaderboard } from '../services/appreciationService';
import { getMyBadges } from '../services/badgeService';

const CultureImpact = () => {
  const { currentUser } = useAppContext();
  const [stats, setStats] = useState({
    sentCount: 0,
    receivedCount: 0,
    badgeCount: 0,
    leaderboardRank: null,
    totalUsers: 0
  });
  const [badges, setBadges] = useState([]);
  const [recentRecognitions, setRecentRecognitions] = useState([]);
  const [signatureValue, setSignatureValue] = useState(null);
  const [weekStreak, setWeekStreak] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCultureImpact = async () => {
      if (!currentUser || !currentUser.username) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Fetch all data in parallel
        const [sentData, receivedData, badgesData, leaderboardData] = await Promise.all([
          getSentAppreciations(currentUser.username),
          getReceivedAppreciations(currentUser.username),
          getMyBadges(),
          getLeaderboard()
        ]);

        // Calculate stats
        const sentCount = sentData.length;
        const receivedCount = receivedData.length;
        const badgeCount = badgesData.length;

        // Find user's rank in leaderboard
        const userRank = leaderboardData.findIndex(
          entry => entry.user_id === currentUser.id
        );
        const leaderboardRank = userRank !== -1 ? userRank + 1 : null;
        const totalUsers = leaderboardData.length;

        setStats({
          sentCount,
          receivedCount,
          badgeCount,
          leaderboardRank,
          totalUsers
        });
        setBadges(badgesData);

        // Get last 5 received appreciations
        const recent = receivedData.slice(0, 5);
        setRecentRecognitions(recent);

        // Calculate signature value from sent appreciations
        if (sentData.length > 0) {
          const valueCounts = {};
          sentData.forEach(appreciation => {
            if (appreciation.company_values && Array.isArray(appreciation.company_values)) {
              appreciation.company_values.forEach(value => {
                valueCounts[value] = (valueCounts[value] || 0) + 1;
              });
            }
          });

          if (Object.keys(valueCounts).length > 0) {
            const topValue = Object.entries(valueCounts)
              .sort(([, a], [, b]) => b - a)[0];
            const percentage = Math.round((topValue[1] / sentData.length) * 100);
            setSignatureValue({ name: topValue[0], percentage });
          }
        }

        // Calculate week streak
        const calculateWeekStreak = (appreciations) => {
          if (appreciations.length === 0) return 0;

          const sortedDates = appreciations
            .map(a => new Date(a.created_at))
            .sort((a, b) => b - a);

          const getWeekNumber = (date) => {
            const startOfYear = new Date(date.getFullYear(), 0, 1);
            const diff = date - startOfYear;
            const oneWeek = 1000 * 60 * 60 * 24 * 7;
            return Math.floor(diff / oneWeek);
          };

          let streak = 1;
          let currentWeek = getWeekNumber(sortedDates[0]);

          for (let i = 1; i < sortedDates.length; i++) {
            const week = getWeekNumber(sortedDates[i]);
            if (week === currentWeek - 1) {
              streak++;
              currentWeek = week;
            } else if (week !== currentWeek) {
              break;
            }
          }

          return streak;
        };

        const streak = calculateWeekStreak(sentData);
        setWeekStreak(streak);
      } catch (error) {
        console.error('Error fetching culture impact:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCultureImpact();
  }, [currentUser]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
        <div className="text-center py-10">Loading your culture impact...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold gradient-text">Your Culture Impact</h1>
        <p className="mt-2 text-base text-gray-600">
          Your recognition story and cultural influence.
        </p>
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 mb-8">
        {/* Week Streak */}
        <div className="bg-white overflow-hidden shadow-md rounded-xl card-hover border-l-4 border-indigo-500 fade-in">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-14 w-14 rounded-full bg-gradient-to-br from-orange-400 to-red-500 text-white shadow-md">
                  <i className="fas fa-fire text-2xl"></i>
                </div>
              </div>
              <div className="ml-5 flex-1">
                <dt className="text-sm font-medium text-gray-500">
                  Recognition Streak
                </dt>
                <dd className="flex items-baseline mt-1">
                  <div className="text-4xl font-bold gradient-text">
                    {weekStreak}
                  </div>
                  <span className="ml-2 text-lg text-gray-600">
                    {weekStreak === 1 ? 'week' : 'weeks'}
                  </span>
                </dd>
              </div>
            </div>
          </div>
        </div>

        {/* Signature Value */}
        {signatureValue && (
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 overflow-hidden shadow-md rounded-xl card-hover border-l-4 border-purple-500 fade-in">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-14 w-14 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 text-white shadow-md">
                    <i className="fas fa-star text-2xl"></i>
                  </div>
                </div>
                <div className="ml-5 flex-1">
                  <dt className="text-sm font-medium text-gray-600">
                    Your Signature Value
                  </dt>
                  <dd className="mt-1">
                    <div className="text-lg font-bold text-gray-900">
                      {signatureValue.name}
                    </div>
                    <div className="text-sm text-gray-600">
                      {signatureValue.percentage}% of your recognitions
                    </div>
                  </dd>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Leaderboard Rank */}
        <div className="bg-white overflow-hidden shadow-md rounded-xl card-hover border-l-4 border-yellow-500 fade-in">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-14 w-14 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 text-white shadow-md">
                  <i className="fas fa-trophy text-2xl"></i>
                </div>
              </div>
              <div className="ml-5 flex-1">
                <dt className="text-sm font-medium text-gray-500">
                  Leaderboard Rank
                </dt>
                <dd className="flex items-baseline mt-1">
                  <div className="text-4xl font-bold gradient-text">
                    {stats.leaderboardRank ? `#${stats.leaderboardRank}` : 'N/A'}
                  </div>
                  {stats.totalUsers > 0 && (
                    <span className="ml-2 text-sm text-gray-600">
                      of {stats.totalUsers}
                    </span>
                  )}
                </dd>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Recognition Feed */}
      {recentRecognitions.length > 0 && (
        <div className="bg-white shadow-md rounded-xl p-6 mb-8 card-hover fade-in">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Recent Recognition</h2>
          <div className="space-y-4">
            {recentRecognitions.map((recognition, index) => (
              <div
                key={index}
                className="border-l-4 border-green-500 bg-gray-50 p-4 rounded-r-lg hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="font-medium text-gray-900">
                    From: {recognition.sender.full_name || recognition.sender.username}
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(recognition.created_at).toLocaleDateString()}
                  </div>
                </div>
                {recognition.company_values && recognition.company_values.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {recognition.company_values.map((value, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                      >
                        {value}
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-sm text-gray-700 italic">"{recognition.message.substring(0, 150)}{recognition.message.length > 150 ? '...' : ''}"</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Badges Section */}
      {badges.length > 0 && (
        <div className="bg-white shadow-md rounded-xl p-6 card-hover fade-in">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Badges</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {badges.map((badge) => (
              <div
                key={badge.id}
                className="border-l-4 border-yellow-500 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-r-xl p-4 flex items-center space-x-3 hover:shadow-lg transition-all"
              >
                <div className="flex-shrink-0">
                  <div className="h-14 w-14 rounded-full bg-gradient-to-br from-yellow-400 to-orange-600 flex items-center justify-center text-white text-2xl shadow-md">
                    <i className="fas fa-medal"></i>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-bold text-gray-900 truncate">
                    {badge.name}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    {badge.description}
                  </p>
                  {badge.earned_at && (
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(badge.earned_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State for Badges */}
      {badges.length === 0 && (
        <div className="bg-white shadow-md rounded-xl p-6 card-hover fade-in">
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 mb-4">
              <i className="fas fa-award text-4xl text-gray-400"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No badges yet</h3>
            <p className="text-base text-gray-600">
              Keep sending and receiving appreciations to earn your first badge!
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CultureImpact;
